import { Helmet } from "react-helmet-async";
import { BRAND, TESTIMONIALS, SERVICES } from "../mock";

export function SEO({
  title,
  description,
  canonical,
  image = "https://customer-assets.emergentagent.com/job_paint-pro-finish/artifacts/s2b2b55t_pup%20face%20joker-01-01%20%282%29.png",
  schemas = [],
  noindex = false,
}) {
  const fullTitle = title.includes("Jester Werks")
    ? title
    : `${title} | Jester Werks Mississauga`;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {canonical && <link rel="canonical" href={canonical} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {!noindex && <meta name="robots" content="index,follow,max-image-preview:large" />}
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:site_name" content="Jester Werks" />
      <meta property="og:locale" content="en_CA" />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      {/* Geo */}
      <meta name="geo.region" content="CA-ON" />
      <meta name="geo.placename" content="Mississauga" />
      <meta name="geo.position" content={`${BRAND.geo.lat};${BRAND.geo.lng}`} />
      <meta name="ICBM" content={`${BRAND.geo.lat}, ${BRAND.geo.lng}`} />
      {/* JSON-LD schemas */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

// === Schema builders ===

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_paint-pro-finish/artifacts/s2b2b55t_pup%20face%20joker-01-01%20%282%29.png";

export function localBusinessSchema(extra = {}) {
  return {
    "@context": "https://schema.org",
    "@type": ["AutoDetailing", "LocalBusiness"],
    "@id": `${BRAND.url}/#business`,
    name: BRAND.name,
    image: LOGO_URL,
    logo: LOGO_URL,
    url: BRAND.url,
    telephone: BRAND.phone,
    email: BRAND.email,
    priceRange: "$$-$$$",
    description:
      "Jester Werks is the GTA's most-reviewed ceramic coating, paint correction and auto detailing studio. Hand-finished 5-year ceramic coatings. Two locations. Mississauga and Vaughan. serving Toronto, Mississauga, Vaughan, Oakville, Brampton and the entire GTA.",
    address: BRAND.locations.map((loc) => ({
      "@type": "PostalAddress",
      streetAddress: loc.street,
      addressLocality: loc.city,
      addressRegion: loc.region,
      postalCode: loc.postal,
      addressCountry: loc.country,
    })),
    location: BRAND.locations.map((loc) => ({
      "@type": "Place",
      name: `${BRAND.name}. ${loc.name}`,
      address: {
        "@type": "PostalAddress",
        streetAddress: loc.street,
        addressLocality: loc.city,
        addressRegion: loc.region,
        postalCode: loc.postal,
        addressCountry: loc.country,
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: loc.geo.lat,
        longitude: loc.geo.lng,
      },
    })),
    geo: {
      "@type": "GeoCoordinates",
      latitude: BRAND.geo.lat,
      longitude: BRAND.geo.lng,
    },
    areaServed: BRAND.serviceAreas.map((c) => ({
      "@type": "City",
      name: c,
    })),
    openingHoursSpecification: BRAND.hoursSchema.map((h) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: h.d,
      opens: h.o,
      closes: h.c,
    })),
    sameAs: [
      BRAND.socials.instagram,
      BRAND.socials.facebook,
      BRAND.socials.google,
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: BRAND.rating.value,
      reviewCount: BRAND.rating.count,
      bestRating: 5,
      worstRating: 1,
    },
    review: TESTIMONIALS.slice(0, 5).map((t) => ({
      "@type": "Review",
      reviewRating: {
        "@type": "Rating",
        ratingValue: t.rating,
        bestRating: 5,
      },
      author: { "@type": "Person", name: t.name },
      reviewBody: t.quote,
      datePublished: t.date,
    })),
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Auto Detailing Services",
      itemListElement: SERVICES.map((s) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: s.title,
          description: s.short,
        },
        price: s.from.replace(/[^\d]/g, ""),
        priceCurrency: "CAD",
      })),
    },
    ...extra,
  };
}

export function serviceSchema({ name, description, areaServed = ["Mississauga", "Toronto"], offers }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: name,
    provider: { "@id": `${BRAND.url}/#business` },
    name,
    description,
    areaServed: areaServed.map((c) => ({ "@type": "City", name: c })),
    ...(offers && { offers: { "@type": "Offer", price: offers, priceCurrency: "CAD" } }),
  };
}

export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}
